import { Card, Flex, Space, Divider, Typography, Row, Col, Tag } from "antd";
import { Navbar } from "../../components/navbar";
import { useState } from "react";
import {
  CloudServerOutlined,
  DesktopOutlined,
  TabletOutlined,
  ThunderboltOutlined,
  CheckCircleFilled,
  StarFilled,
  RocketOutlined,
  SafetyCertificateOutlined,
  LockOutlined,
  EyeOutlined,
  BookOutlined,
  InteractionOutlined,
} from "@ant-design/icons";
import { FooterSection2 } from "../../layouts/footer_section_2";
import { Head, Link } from "@inertiajs/react";

const { Title, Text, Paragraph } = Typography;

const ACCENT = "#4096ff";
const ACCENT_LIGHT = "#4096ff15";
const TEXT_PRIMARY = "#1a2332";
const TEXT_SECONDARY = "#556678";

const fiturUtama = [
  {
    title: "Interactive Storytelling",
    desc: "Pembelajaran multimedia yang menarik melalui cerita interaktif, menjadikan anak-anak sebagai bagian aktif dalam setiap narasi edukasi.",
    icon: <InteractionOutlined />,
  },
  {
    title: "Personal Assessment",
    desc: "Sistem evaluasi yang disesuaikan dengan kemampuan masing-masing anak, memberikan laporan progres belajar secara personal.",
    icon: <BookOutlined />,
  },
];

const products = [
  {
    key: "edge",
    name: "Kiversa Edge",
    badge: "Zero-Connectivity",
    badgeColor: "#52c41a",
    tagline: "Solusi Tanpa Internet",
    description:
      "Paket All-in-One untuk wilayah dengan akses internet terbatas. Beroperasi sepenuhnya offline dengan perangkat keras tangguh.",
    highlights: [
      "Perangkat stand-alone + penyimpanan lokal",
      "Kurikulum digital tanpa jeda (zero latency)",
      "Tanpa sinyal seluler atau Wi-Fi",
      "Cocok untuk sekolah pedalaman",
    ],
    bottom: {
      type: "offline",
      label: "100% Offline",
      desc: "Tanpa koneksi internet",
      icon: <LockOutlined />,
    },
    icon: <CloudServerOutlined />,
    color: "#52c41a",
  },
  {
    key: "flexi",
    name: "Kiversa Flexi-Sub",
    badge: "Langganan",
    badgeColor: ACCENT,
    tagline: "Transformasi Digital Fleksibel",
    description:
      "Model langganan berkala (per 3 bulan) yang memaksimalkan perangkat sudah ada di institusi Anda.",
    highlights: [
      "Langganan per 3 bulan",
      "Maksimalkan perangkat tersedia",
      "Tiga mode akses komprehensif",
      "Skalabel sesuai kebutuhan",
    ],
    bottom: {
      type: "tags",
      items: [
        { label: "Touch-TV", color: "#722ed1", icon: <DesktopOutlined /> },
        { label: "Desktop", color: "#eb2f96", icon: <DesktopOutlined /> },
        { label: "Tablet", color: "#fa8c16", icon: <TabletOutlined /> },
      ],
    },
    icon: <RocketOutlined />,
    color: ACCENT,
  },
  {
    key: "pro",
    name: "Kiversa Pro-License",
    badge: "Lisensi Khusus",
    badgeColor: "#722ed1",
    tagline: "Dedicated Desktop",
    description:
      "Lisensi khusus untuk PC Monitor layar sentuh dengan stabilitas dan performa tinggi.",
    highlights: [
      "Optimasi sentuhan tingkat tinggi",
      "Grafis multimedia tajam",
      "Responsivitas maksimal",
      "Stabilitas untuk penggunaan intensif",
    ],
    bottom: {
      type: "tags",
      items: [
        { label: "Perpustakaan Digital", color: "#722ed1" },
        { label: "Pojok Baca Mandiri", color: "#722ed1" },
        { label: "Stasiun Interaktif", color: "#722ed1" },
      ],
    },
    icon: <SafetyCertificateOutlined />,
    color: "#722ed1",
  },
];

function CompactProductCard({ product }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        height: "100%",
        opacity: hovered ? 1 : 0.92,
        transform: hovered ? "translateY(-6px)" : "translateY(0)",
        transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      <Card
        bordered={false}
        style={{
          height: "100%",
          borderRadius: 20,
          background: "#ffffff",
          boxShadow: hovered
            ? `0 16px 48px ${product.color}20`
            : "0 2px 16px rgba(0, 0, 0, 0.05)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
        bodyStyle={{
          padding: 0,
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        {/* Colored top bar */}
        <div
          style={{
            height: 5,
            background: `linear-gradient(90deg, ${product.color}, ${product.color}aa)`,
            flexShrink: 0,
          }}
        />

        <div
          style={{
            padding: "24px 24px 20px",
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header: icon + name + badge */}
          <Flex align="start" justify="space-between" gap={12}>
            <Flex align="center" gap={12}>
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 12,
                  background: `${product.color}12`,
                  color: product.color,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 20,
                  flexShrink: 0,
                }}
              >
                {product.icon}
              </div>
              <div>
                <Title
                  level={5}
                  style={{
                    margin: 0,
                    color: TEXT_PRIMARY,
                    fontFamily: "'Quicksand', sans-serif",
                    fontWeight: 700,
                    fontSize: 17,
                    lineHeight: 1.3,
                  }}
                >
                  {product.name}
                </Title>
                <Text
                  style={{
                    fontSize: 12,
                    color: TEXT_SECONDARY,
                    fontFamily: "'Quicksand', sans-serif",
                  }}
                >
                  {product.tagline}
                </Text>
              </div>
            </Flex>
            <Tag
              style={{
                borderRadius: 16,
                padding: "2px 10px",
                fontSize: 11,
                fontWeight: 600,
                fontFamily: "'Quicksand', sans-serif",
                background: `${product.color}14`,
                color: product.color,
                border: `1px solid ${product.color}30`,
                flexShrink: 0,
                marginTop: 2,
              }}
            >
              {product.badge}
            </Tag>
          </Flex>

          {/* Description */}
          <Paragraph
            style={{
              margin: "14px 0 14px",
              fontSize: 13,
              lineHeight: 1.7,
              color: TEXT_SECONDARY,
              fontFamily: "'Quicksand', sans-serif",
              flexShrink: 0,
            }}
          >
            {product.description}
          </Paragraph>

          {/* Highlights */}
          <Space direction="vertical" size={7} style={{ flex: 1 }}>
            {product.highlights.map((h, i) => (
              <Flex key={i} align="center" gap={8}>
                <CheckCircleFilled
                  style={{
                    color: product.color,
                    fontSize: 13,
                    flexShrink: 0,
                  }}
                />
                <Text
                  style={{
                    fontSize: 12.5,
                    lineHeight: 1.5,
                    color: TEXT_SECONDARY,
                    fontFamily: "'Quicksand', sans-serif",
                  }}
                >
                  {h}
                </Text>
              </Flex>
            ))}
          </Space>

          {/* Bottom section */}
          <Divider
            style={{
              margin: "14px 0 12px",
              borderColor: `${product.color}18`,
            }}
          />

          {product.bottom.type === "offline" && (
            <Flex align="center" gap={10}>
              <div
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 8,
                  background: `${product.color}14`,
                  color: product.color,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 14,
                  flexShrink: 0,
                }}
              >
                {product.bottom.icon}
              </div>
              <div>
                <Text
                  strong
                  style={{
                    fontSize: 12,
                    color: TEXT_PRIMARY,
                    fontFamily: "'Quicksand', sans-serif",
                    display: "block",
                    lineHeight: 1.3,
                  }}
                >
                  {product.bottom.label}
                </Text>
                <Text
                  style={{
                    fontSize: 11,
                    color: TEXT_SECONDARY,
                    fontFamily: "'Quicksand', sans-serif",
                  }}
                >
                  {product.bottom.desc}
                </Text>
              </div>
            </Flex>
          )}

          {product.bottom.type === "tags" && (
            <Flex wrap="wrap" gap={6}>
              {product.bottom.items.map((item, i) => (
                <Tag
                  key={i}
                  style={{
                    borderRadius: 14,
                    padding: "3px 10px",
                    fontSize: 11,
                    fontFamily: "'Quicksand', sans-serif",
                    background: `${item.color}0d`,
                    color: item.color,
                    border: `1px solid ${item.color}25`,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  {item.icon}
                  {item.label}
                </Tag>
              ))}
            </Flex>
          )}
        </div>
      </Card>
    </div>
  );
}

function Product(params) {
  return (
    <>
      <Head>
        <title>Kidversa - Produk</title>
      </Head>
      <Flex
        vertical
        align="center"
        style={{
          minHeight: "100vh",
          background:
            "linear-gradient(180deg, #f8fafc 0%, #eef2f7 50%, #ffffff 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative blobs */}
        <div
          style={{
            position: "absolute",
            top: 60,
            left: -150,
            width: 450,
            height: 450,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(64,150,255,0.07) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: 300,
            right: -120,
            width: 380,
            height: 380,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(82,196,26,0.06) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: 200,
            left: -80,
            width: 350,
            height: 350,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(114,46,209,0.06) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />

        <Navbar />

        <main
          style={{
            width: "100%",
            maxWidth: 1200,
            margin: "0 auto",
            padding: "120px 24px 80px",
          }}
        >
          {/* Hero Section */}
          <Space
            direction="vertical"
            align="center"
            size="small"
            style={{ width: "100%", marginBottom: 64 }}
          >
            <Title
              level={1}
              style={{
                margin: "16px 0 0",
                fontSize: "clamp(32px, 5vw, 48px)",
                fontWeight: 800,
                color: TEXT_PRIMARY,
                textAlign: "center",
                fontFamily: "'Quicksand', sans-serif",
                letterSpacing: "-0.02em",
              }}
            >
              Solusi Produk <span style={{ color: ACCENT }}>Kiversa</span>
            </Title>
            <Paragraph
              style={{
                maxWidth: 680,
                textAlign: "center",
                color: TEXT_SECONDARY,
                fontSize: 16,
                marginTop: 16,
                fontFamily: "'Quicksand', sans-serif",
                lineHeight: 1.8,
              }}
            >
              Merevolusi masa depan pendidikan interaktif. Tiga produk solusi
              strategis yang dirancang untuk menjembatani kesenjangan digital
              dan memastikan pendidikan berkualitas dapat diakses setiap anak.
            </Paragraph>
          </Space>

          {/* Fitur Utama - 3 kolom kecil */}
          <Row gutter={[24, 24]} justify="center" style={{ marginBottom: 80 }}>
            {fiturUtama.map((item, index) => (
              <Col xs={24} sm={12} lg={8} key={index}>
                <Card
                  bordered={false}
                  style={{
                    height: "100%",
                    borderRadius: 16,
                    background: "#ffffff",
                    boxShadow: "0 2px 12px rgba(0, 0, 0, 0.04)",
                  }}
                  bodyStyle={{ padding: 28 }}
                  hoverable
                >
                  <Space direction="vertical" size="middle">
                    <div
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: 12,
                        background: ACCENT_LIGHT,
                        color: ACCENT,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 20,
                      }}
                    >
                      {item.icon}
                    </div>
                    <Text
                      strong
                      style={{
                        fontSize: 16,
                        color: TEXT_PRIMARY,
                        fontFamily: "'Quicksand', sans-serif",
                        display: "block",
                        marginBottom: 4,
                      }}
                    >
                      {item.title}
                    </Text>
                    <Paragraph
                      style={{
                        margin: 0,
                        fontSize: 14,
                        lineHeight: 1.7,
                        color: TEXT_SECONDARY,
                        fontFamily: "'Quicksand', sans-serif",
                      }}
                    >
                      {item.desc}
                    </Paragraph>
                  </Space>
                </Card>
              </Col>
            ))}
            <Col xs={24} sm={12} lg={8}>
              <Card
                bordered={false}
                style={{
                  height: "100%",
                  borderRadius: 16,
                  background: `linear-gradient(135deg, ${ACCENT} 0%, #1677ff 100%)`,
                  boxShadow: "0 4px 20px rgba(64, 150, 255, 0.25)",
                }}
                bodyStyle={{ padding: 28 }}
              >
                <Space direction="vertical" size="middle">
                  <div
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: 12,
                      background: "rgba(255,255,255,0.2)",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 20,
                    }}
                  >
                    <ThunderboltOutlined />
                  </div>
                  <Text
                    strong
                    style={{
                      fontSize: 16,
                      color: "#fff",
                      fontFamily: "'Quicksand', sans-serif",
                      display: "block",
                      marginBottom: 4,
                    }}
                  >
                    Ekosistem Terintegrasi
                  </Text>
                  <Paragraph
                    style={{
                      margin: 0,
                      fontSize: 14,
                      lineHeight: 1.7,
                      color: "rgba(255,255,255,0.85)",
                      fontFamily: "'Quicksand', sans-serif",
                    }}
                  >
                    Semua produk saling melengkapi dalam satu ekosistem
                    pembelajaran yang konsisten dan dapat diskalakan sesuai
                    kebutuhan institusi Anda.
                  </Paragraph>
                </Space>
              </Card>
            </Col>
          </Row>

          {/* Section Label */}
          <Space
            direction="vertical"
            align="center"
            size="small"
            style={{ width: "100%", marginBottom: 32 }}
          >
            <Text
              strong
              style={{
                color: ACCENT,
                fontSize: 14,
                textTransform: "uppercase",
                letterSpacing: "0.15em",
                fontFamily: "'Quicksand', sans-serif",
              }}
            >
              Pilih Solusi Anda
            </Text>
            <Title
              level={2}
              style={{
                margin: 0,
                color: TEXT_PRIMARY,
                fontFamily: "'Quicksand', sans-serif",
                fontWeight: 700,
                textAlign: "center",
              }}
            >
              Tiga Produk Strategis
            </Title>
          </Space>

          {/* Compact Horizontal Product Cards */}
          <Row gutter={[24, 24]} justify="center">
            {products.map((product) => (
              <Col xs={24} md={12} xl={8} key={product.key}>
                <CompactProductCard product={product} />
              </Col>
            ))}
          </Row>

          {/* Bottom CTA */}
          <Row justify="center" style={{ marginTop: 80 }}>
            <Col xs={24} lg={16}>
              <Card
                bordered={false}
                style={{
                  borderRadius: 24,
                  background: `linear-gradient(135deg, #f0f7ff 0%, #f5f0ff 50%, #f0fff4 100%)`,
                  textAlign: "center",
                  border: `1px solid ${ACCENT}15`,
                }}
                bodyStyle={{ padding: "48px 40px" }}
              >
                <Space direction="vertical" size="middle" align="center">
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 16,
                      background: ACCENT_LIGHT,
                      color: ACCENT,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 24,
                    }}
                  >
                    <EyeOutlined />
                  </div>
                  <Title
                    level={3}
                    style={{
                      margin: 0,
                      color: TEXT_PRIMARY,
                      fontFamily: "'Quicksand', sans-serif",
                      fontWeight: 700,
                    }}
                  >
                    Tertarik dengan Solusi Kami?
                  </Title>
                  <Paragraph
                    style={{
                      margin: 0,
                      maxWidth: 520,
                      color: TEXT_SECONDARY,
                      fontSize: 15,
                      fontFamily: "'Quicksand', sans-serif",
                      lineHeight: 1.7,
                    }}
                  >
                    Konsultasikan kebutuhan institusi Anda dengan tim kami. Kami
                    siap membantu memilih solusi terbaik untuk lingkungan
                    pembelajaran anak-anak Anda.
                  </Paragraph>
                  <Space size={16} style={{ marginTop: 8 }}>
                    <Link
                      href="/contact"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "12px 28px",
                        borderRadius: 12,
                        background: ACCENT,
                        color: "#fff",
                        fontWeight: 600,
                        fontSize: 14,
                        fontFamily: "'Quicksand', sans-serif",
                        textDecoration: "none",
                        boxShadow: "0 4px 14px rgba(64,150,255,0.35)",
                        transition: "all 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow =
                          "0 6px 20px rgba(64,150,255,0.45)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow =
                          "0 4px 14px rgba(64,150,255,0.35)";
                      }}
                    >
                      Hubungi Kami
                    </Link>
                    <Link
                      href="/about"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "12px 28px",
                        borderRadius: 12,
                        background: "#fff",
                        color: TEXT_PRIMARY,
                        fontWeight: 600,
                        fontSize: 14,
                        fontFamily: "'Quicksand', sans-serif",
                        textDecoration: "none",
                        border: `1px solid ${ACCENT}30`,
                        transition: "all 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = ACCENT;
                        e.currentTarget.style.color = ACCENT;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = `${ACCENT}30`;
                        e.currentTarget.style.color = TEXT_PRIMARY;
                      }}
                    >
                      Tentang Kami
                    </Link>
                  </Space>
                </Space>
              </Card>
            </Col>
          </Row>
        </main>

        <FooterSection2 />
      </Flex>
    </>
  );
}

export default Product;
